import { useId } from 'react';
import { cn } from '@/components/ui/cn';
import { areaPath, linearScale, monotonePath } from './scale';

/*
  Sparkline — мини-график тренда без осей (в Stat-плитках, строках списков).
  Та же плавная кривая и та же градиентная размывка, что у LineChart, — одна семья.
  tone: muted — второстепенный тренд (по умолчанию в плитках), brand — акцентный.
*/

interface SparklineProps {
  values: number[];
  width?: number;
  height?: number;
  tone?: 'muted' | 'brand' | 'danger' | 'success';
  /** Точка на последнем значении. */
  endDot?: boolean;
  /** Подложка: растягивается по контейнеру (толщина линии при этом не плывёт). */
  fluid?: boolean;
  className?: string;
}

const strokeVar: Record<NonNullable<SparklineProps['tone']>, string> = {
  muted: 'var(--fg-subtle)',
  brand: 'var(--brand)',
  danger: 'var(--danger)',
  success: 'var(--success)',
};

export function Sparkline({
  values,
  width = 96,
  height = 28,
  tone = 'muted',
  endDot = true,
  fluid = false,
  className,
}: SparklineProps) {
  const id = useId();
  if (values.length < 2) return null;
  const pad = 3;
  const min = Math.min(...values);
  const max = Math.max(...values);
  const x = linearScale([0, values.length - 1], [pad, width - pad]);
  const y = linearScale([min, max === min ? min + 1 : max], [height - pad, pad]);
  const pts = values.map((v, i) => ({ x: x(i), y: y(v) }));
  const color = strokeVar[tone];
  const last = pts[pts.length - 1];
  return (
    <svg
      width={fluid ? undefined : width}
      height={fluid ? undefined : height}
      viewBox={`0 0 ${width} ${height}`}
      preserveAspectRatio={fluid ? 'none' : undefined}
      className={cn(fluid ? 'size-full' : 'shrink-0 overflow-visible', className)}
      aria-hidden
    >
      <defs>
        <linearGradient id={id} x1="0" y1="0" x2="0" y2="1">
          <stop offset="0%" stopColor={color} stopOpacity={tone === 'muted' ? 0.12 : 0.22} />
          <stop offset="100%" stopColor={color} stopOpacity={0} />
        </linearGradient>
      </defs>
      <path d={areaPath(pts, height - pad)} fill={`url(#${id})`} />
      <path
        d={monotonePath(pts)}
        fill="none"
        stroke={color}
        strokeWidth={1.5}
        strokeLinecap="round"
        strokeLinejoin="round"
        vectorEffect={fluid ? 'non-scaling-stroke' : undefined}
      />
      {endDot && (
        <circle cx={last.x} cy={last.y} r={2.5} fill={color} stroke="var(--card)" strokeWidth={1.5} />
      )}
    </svg>
  );
}
